import ResponseViewer, { getResponseTabs } from '../ui/ResponseViewer';
import TransferSizeHint from '../ui/TransferSizeHint';
import AskAiButton from '../aiChat/AskAiButton';
import { buildRequestFailurePrompt } from '../../services/ai/failurePrompts';
import HttpEventStreamView, { HttpEventStreamState } from '../HttpEventStreamView';

/** The response pane (plain render function, called where the markup was). */
export function renderResponsePane({
  response, testResults, selectResponseViewerTab, responseViewerTab, initialData, method, url, name,
  aiReadResponses, eventStream, loading, selectedEnvironment, globalVariables, splitLayoutTick,
  loaderNode, testScriptErrors, tabFieldsRef, sendHttpRequest, t, shortcutHints,
}) {
  return (
    <div className="bg-white dark:bg-[#161618] flex flex-col h-full response-section" style={{ minHeight: 0 }}>
      {/* Tab bar — always visible when a response exists so it never blinks away */}
      {response && (
        <div className="request-response-meta-bar border-b border-gray-200 bg-white px-5 dark:border-zinc-700 dark:bg-[#161618]">
          <div className="mt-2 flex items-center justify-between gap-4 text-2xs">
            <nav className="flex flex-1 min-w-0">
              {getResponseTabs(response, testResults).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => selectResponseViewerTab(tab.key)}
                  className={`pr-3 pt-1 pb-1 bg-white font-medium transition-all duration-200 ${
                    responseViewerTab === tab.key
                      ? 'text-blue-600 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-transparent'
                  }`}
                >
                  <div className="flex items-center space-x-1">
                    <span>{tab.label}</span>
                    {tab.count !== null && (
                      <span
                        className={`px-1 py-1 rounded-full text-xxs ${
                          responseViewerTab === tab.key
                            ? 'bg-blue-200 text-blue-800 response-tab-count--active'
                            : 'bg-gray-200 text-gray-600 response-tab-count--inactive'
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </nav>
            <div className="flex items-center space-x-3 flex-shrink-0">
              <span
                className={`px-2 py-1 rounded-full font-medium ${
                  response.status >= 200 && response.status < 300
                    ? 'status-success'
                    : response.status >= 400 || response.status === 0
                      ? 'status-error'
                      : 'status-warning'
                }`}
              >
                {response.status} {response.statusText}
              </span>
              {/* an event stream says so HERE, in the response's one status row */}
              {response.streaming && <HttpEventStreamState tabId={initialData?.id} />}
              <span className="text-gray-600 font-medium">{response.time}ms</span>
              <TransferSizeHint response={response} />
              {/* Only on a failure. Offering "Explain" beside a healthy
                  200 is noise; beside a 401 it is the next thing the
                  user was going to do anyway. */}
              {(response.status >= 400 || response.status === 0) && (
                <AskAiButton
                  label="Explain"
                  title="Ask the AI assistant why this request failed"
                  prompt={buildRequestFailurePrompt({ response, method, url, name, allowBody: aiReadResponses })}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Content area — no outer overlay; overlay lives inside ResponseViewer */}
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        {response?.streaming && eventStream.status !== 'idle' && responseViewerTab === 'body' && !loading ? (
          <HttpEventStreamView tabId={initialData?.id} />
        ) : response ? (
          <ResponseViewer
            response={response}
            selectedEnvironment={selectedEnvironment}
            globalVariables={globalVariables}
            activeTab={responseViewerTab}
            splitLayoutTick={splitLayoutTick}
            loading={loading}
            loadingContent={loading ? loaderNode : null}
            testResults={testResults}
            testScriptErrors={testScriptErrors}
            hasTestScript={!!(tabFieldsRef.current.testScript?.trim())}
            onRetry={url ? sendHttpRequest : undefined}
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center h-full" style={{ gap: 4 }}>
            {loaderNode}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 px-5">
            <ul
              className="request-shortcut-hints"
              aria-label={t('request.keyboardShortcuts')}
            >
              {shortcutHints.map(({ label, keys }) => (
                <li
                  key={label}
                  className="flex items-center justify-center gap-3 text-xs text-gray-500 whitespace-nowrap"
                >
                  <span className="w-[6.75rem] shrink-0 text-right">{label}</span>
                  <kbd className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] font-medium text-gray-600 shadow-sm">
                    {keys}
                  </kbd>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
